/**
 * 허용 지점 선택 모달 (제품 에디터 SKU).
 * Figma: 307:4209 (선택 상태).
 *
 * 구조 (680×880):
 *  - 좌측 (200w): [활성 상태] + [브랜드(보유 계정만, location.brandId 매칭)] + 도시별 카테고리 탭
 *  - 우측 (480w): 검색 + (본사만 보기 토글 + 모두 펼치기 버튼) + 지점 리스트 + 액션 버튼
 *
 * 도시 분류 (우진 안내: 8개 시 + 기타):
 *   서울/부산/대구/인천/광주/대전/울산/세종 + 기타
 *   location.address 첫부분 매칭으로 그룹화.
 *
 * Suspect:
 *  - "본사만 보기" 토글의 동작: 본사(headquarter) 지점만 필터 (location.type 또는 isHeadquarter 필드 기준).
 *    현재 데이터에 type === "indirect" / "direct" 가 있어 todo 처리.
 *  - 그룹 내 "전체 선택" — 도시 전체 toggle 기능 미정. 현재는 좌측 도시 탭 선택만 필터 역할.
 *  - city tab 의 count: 해당 도시 location 수
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { ChevronDown, ChevronUp } from "lucide-react";

import { DSModal, DSButton, DSSearchInput, DSCheckbox } from "./ds";
import { getBrands } from "../reducers";

// ---------------------------------------------------------------------
// City grouping
// ---------------------------------------------------------------------
// suspect: 한국 8개 광역시 + 특별시. 데이터에 따라 매칭이 안 될 수도 있어 "기타" fallback.
const KOREAN_CITIES = [
  "서울",
  "부산",
  "대구",
  "인천",
  "광주",
  "대전",
  "울산",
  "세종",
];

const detectCity = (address) => {
  if (!address) return "기타";
  const addr = String(address).trim();
  for (const city of KOREAN_CITIES) {
    // "서울시", "서울특별시" 등 prefix 모두 매칭
    if (addr.startsWith(city)) return city;
  }
  return "기타";
};

const groupLocationsByCity = (locations) => {
  const groups = {};
  KOREAN_CITIES.forEach((c) => (groups[c] = []));
  groups["기타"] = [];
  (locations || []).forEach((loc) => {
    const city = detectCity(loc.address);
    groups[city].push(loc);
  });
  return groups;
};

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------
const CheckboxVisual = ({ checked }) => (
  <span
    className={`ds-checkbox__box ${checked ? "ds-checkbox__box--checked" : ""}`}
  >
    {checked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
  </span>
);

/** 도시 탭 (figma 340:6723 "menu") */
const CityMenuItem = ({ name, count, active, onClick }) => (
  <div
    className={`ds-city-menu-item ${active ? "ds-city-menu-item--active" : ""}`}
    onClick={onClick}
  >
    <span className="ds-city-menu-item__name">{name}</span>
    <span className="ds-city-menu-item__count">{count}</span>
  </div>
);

// 좌측 그룹 헤더 (figma 503:5919/5924 — Caption/1 SemiBold 12/18, Text/Disabled)
const SectionHeader = ({ children }) => (
  <div className="ds-branch-modal__section-header">{children}</div>
);

/** 매장 행 (figma 307:5414 "category-discription") — bg Paper, pl 64, 체크 + 이름 + 주소 */
const BranchRow = ({ location, isChecked, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "var(--ds-bg-paper)",
      padding: "12px 24px 12px 64px",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  >
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CheckboxVisual checked={isChecked} />
        <span
          style={{
            fontSize: "var(--ds-font-size-14)",
            lineHeight: "var(--ds-line-height-22)",
            color: "var(--ds-text-secondary)",
          }}
        >
          {location.name}
        </span>
      </div>
      {location.address && (
        <div
          style={{
            paddingLeft: 24,
            fontSize: "var(--ds-font-size-12)",
            lineHeight: "var(--ds-line-height-18)",
            color: "var(--ds-text-tertiary)",
          }}
        >
          {location.address}
        </div>
      )}
    </div>
  </div>
);

/** 지역 헤더 (figma 307:4658 "category-noraml") — bg White, 체크 + 지역명 + n/전체 + chevron */
const RegionHeader = ({
  name,
  selectedCount,
  total,
  checked,
  partial,
  collapsed,
  onToggleAll,
  onToggleCollapse,
  // 단일/상한 선택 모드에서는 지역 일괄 선택 체크박스를 노출하지 않는다
  showCheckbox = true,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "var(--ds-bg-white)",
      borderBottom: "1px solid var(--ds-bg-paper)",
      padding: "16px 24px",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        cursor: showCheckbox ? "pointer" : "default",
      }}
      onClick={showCheckbox ? onToggleAll : undefined}
    >
      {showCheckbox && (
        <span
          className={`ds-checkbox__box ${checked ? "ds-checkbox__box--checked" : ""}`}
        >
          {checked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
          {!checked && partial && (
            <FontAwesomeIcon
              icon={faMinus}
              style={{
                fontSize: 10,
                display: "block",
                color: "var(--ds-text-secondary)",
              }}
            />
          )}
        </span>
      )}
      <span
        style={{
          fontSize: "var(--ds-font-size-14)",
          lineHeight: "var(--ds-line-height-22)",
          color: "var(--ds-text-secondary)",
        }}
      >
        {name}
      </span>
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        cursor: "pointer",
      }}
      onClick={onToggleCollapse}
    >
      <span
        style={{
          fontSize: "var(--ds-font-size-12)",
          lineHeight: "var(--ds-line-height-18)",
          color: "var(--ds-text-secondary)",
          textAlign: "right",
        }}
      >
        {selectedCount}{" "}
        <span style={{ color: "var(--ds-text-tertiary)" }}>/ {total}</span>
      </span>
      {collapsed ? (
        <ChevronDown size={14} color="var(--ds-text-tertiary)" />
      ) : (
        <ChevronUp size={14} color="var(--ds-text-tertiary)" />
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------
const BranchSelectModal = ({
  open,
  onClose,
  locations = [],
  selectedIds = [],
  onApply,
  title = "허용 매장 선택",
  defaultAllWhenEmpty = true, // selectedIds 비어있을 때 전체 pre-select (제품=전체허용). 이동 받는곳=false
  // 좌측 하단 "신규 지점 자동 포함" 체크박스 — 제품 허용지점 전용(figma 307:5234).
  // onIncludeFutureChange 가 주어진 경우에만 렌더. 비품(이동/조회)은 미전달 → footer 없음.
  includeFuture,
  onIncludeFutureChange,
  // 좌측에 "활성 상태"(활성/비활성 지점) 그룹 노출 (제품 허용지점 전용, figma 503:5916).
  // location.state === "active" | "inactive" 기준. 비품 모달은 미전달 → 지역 탭만.
  showActiveFilter = false,
  // 단일 선택 모드 (리포트 매장 필터 전용) — 지점 클릭 시 기존 선택을 대체하고,
  // 전체/지역 일괄 선택은 동작하지 않는다.
  singleSelect = false,
  // 선택 상한 (리포트 히트맵 매장 필터 전용, 예: 5) — 상한 도달 시 추가 선택 무시,
  // 전체/지역 일괄 선택 UI 미노출.
  maxSelected = null,
  // 커스텀 그룹 (리포트 국가 선택 전용) — [{key, name, items:[{id,name,address?}]}].
  // 전달 시 주소 기반 도시 그룹 대신 이 그룹으로 좌측 카테고리/우측 리스트를 구성한다.
  customGroups = null,
  searchPlaceholder = "매장 검색",
  emptyText = "매장이 없습니다.",
}) => {
  const [tempSelected, setTempSelected] = useState(() => new Set(selectedIds));
  const [tempIncludeFuture, setTempIncludeFuture] = useState(!!includeFuture);
  const [activeCity, setActiveCity] = useState("__all__"); // "__all__" or city name
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set()); // 접힌 지역 key

  // 모달 오픈 시: selectedIds 비어있으면 "전체 허용" 의미로 모든 지점 pre-select
  // (서버는 futureLocationAvailable=true 일 때 locationIds 비어있어도 전체 허용으로 처리하지만,
  //  UI는 빈 상태가 헷갈리므로 명시적으로 전체 선택 상태로 표시)
  useEffect(() => {
    if (open) {
      const initial =
        selectedIds.length === 0 && defaultAllWhenEmpty
          ? new Set((locations || []).map((l) => l.id))
          : new Set(selectedIds);
      setTempSelected(initial);
      setTempIncludeFuture(!!includeFuture);
      setActiveCity("__all__");
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 브랜드 보유 계정: location.brandId 와 매칭되는 브랜드를 지역 위에 탭으로 노출.
  // 브랜드 데이터(store.brandsById)가 로드되지 않았거나 매칭 지점이 없으면 섹션 자체가 숨는다.
  const brands = useSelector(getBrands);
  const brandGroups = useMemo(() => {
    if (customGroups) return [];
    return (brands || [])
      .map((b) => ({
        key: `__brand__${b.id}`,
        name: b.name,
        items: (locations || []).filter((l) => l.brandId === b.id),
      }))
      .filter((g) => g.items.length > 0);
  }, [brands, locations, customGroups]);

  // 도시별(기본) 또는 커스텀 그룹
  const groups = useMemo(() => {
    if (customGroups) {
      const out = {};
      customGroups.forEach((g) => (out[g.key] = g.items || []));
      return out;
    }
    return groupLocationsByCity(locations);
  }, [locations, customGroups]);

  // 그룹별 카운트
  const cityCounts = useMemo(() => {
    const counts = { __all__: (locations || []).length };
    if (customGroups) {
      customGroups.forEach((g) => (counts[g.key] = (g.items || []).length));
      return counts;
    }
    KOREAN_CITIES.forEach((c) => (counts[c] = groups[c].length));
    counts["기타"] = groups["기타"].length;
    return counts;
  }, [groups, locations, customGroups]);

  // 활성/비활성 매장 카운트 (location.state)
  const activeCount = useMemo(
    () => (locations || []).filter((l) => l.state !== "inactive").length,
    [locations],
  );
  const inactiveCount = useMemo(
    () => (locations || []).filter((l) => l.state === "inactive").length,
    [locations],
  );

  // 표시 그룹 목록 (count > 0 인 것만 + "전체" 항상)
  const visibleCities = useMemo(() => {
    const cities = [
      { key: "__all__", name: "전체", count: cityCounts.__all__ },
    ];
    if (customGroups) {
      customGroups.forEach((g) => {
        if (cityCounts[g.key] > 0)
          cities.push({ key: g.key, name: g.name, count: cityCounts[g.key] });
      });
      return cities;
    }
    KOREAN_CITIES.forEach((c) => {
      if (cityCounts[c] > 0)
        cities.push({ key: c, name: c, count: cityCounts[c] });
    });
    if (cityCounts["기타"] > 0)
      cities.push({ key: "기타", name: "기타", count: cityCounts["기타"] });
    return cities;
  }, [cityCounts, customGroups]);

  // 우측 영역 필터링된 지점 리스트
  const filteredLocations = useMemo(() => {
    let list = locations || [];
    if (activeCity === "__active__") {
      list = list.filter((l) => l.state !== "inactive");
    } else if (activeCity === "__inactive__") {
      list = list.filter((l) => l.state === "inactive");
    } else if (activeCity.startsWith("__brand__")) {
      const group = brandGroups.find((g) => g.key === activeCity);
      list = group ? group.items : [];
    } else if (activeCity !== "__all__") {
      list = groups[activeCity] || [];
    }
    if (search.trim()) {
      const lower = search.toLowerCase();
      list = list.filter(
        (loc) =>
          (loc.name || "").toLowerCase().includes(lower) ||
          (loc.address || "").toLowerCase().includes(lower),
      );
    }
    return list;
  }, [locations, groups, brandGroups, activeCity, search]);

  // 전체 선택 (현재 필터 적용된 리스트 기준)
  const filteredIds = useMemo(
    () => filteredLocations.map((l) => l.id),
    [filteredLocations],
  );

  // 지역별 그룹 (전체 탭 subtree). 검색 시 지역 내에서 필터. 주소 없는 곳은 "기타".
  const regionGroups = useMemo(() => {
    const out = [];
    const lower = search.trim().toLowerCase();
    const pushRegion = (key, name = key) => {
      let items = groups[key] || [];
      if (lower) {
        items = items.filter(
          (l) =>
            (l.name || "").toLowerCase().includes(lower) ||
            (l.address || "").toLowerCase().includes(lower),
        );
      }
      if (items.length) out.push({ key, name, items });
    };
    if (customGroups) {
      customGroups.forEach((g) => pushRegion(g.key, g.name));
      return out;
    }
    KOREAN_CITIES.forEach((c) => pushRegion(c));
    pushRegion("기타");
    return out;
  }, [groups, search, customGroups]);

  const toggleRegionAll = useCallback(
    (items) => {
      if (singleSelect || maxSelected != null) return;
      setTempSelected((prev) => {
        const next = new Set(prev);
        const all = items.every((l) => next.has(l.id));
        items.forEach((l) => (all ? next.delete(l.id) : next.add(l.id)));
        return next;
      });
    },
    [singleSelect, maxSelected],
  );

  const toggleCollapse = useCallback((key) => {
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }, []);

  const allCollapsed =
    regionGroups.length > 0 && regionGroups.every((r) => collapsed.has(r.key));
  const toggleCollapseAll = useCallback(() => {
    setCollapsed(
      allCollapsed ? new Set() : new Set(regionGroups.map((r) => r.key)),
    );
  }, [allCollapsed, regionGroups]);
  const isAllFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => tempSelected.has(id));

  // 핸들러
  const handleToggle = useCallback(
    (id) => {
      setTempSelected((prev) => {
        if (singleSelect) {
          return prev.has(id) ? new Set() : new Set([id]);
        }
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else if (maxSelected != null && next.size >= maxSelected) return prev;
        else next.add(id);
        return next;
      });
    },
    [singleSelect, maxSelected],
  );

  const handleSelectAllFiltered = useCallback(
    (checked) => {
      if (singleSelect) return;
      setTempSelected((prev) => {
        const next = new Set(prev);
        if (checked) filteredIds.forEach((id) => next.add(id));
        else filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    },
    [filteredIds, singleSelect],
  );

  const handleApply = useCallback(() => {
    onApply && onApply(Array.from(tempSelected), tempIncludeFuture);
    onClose && onClose();
  }, [onApply, onClose, tempIncludeFuture, tempSelected]);

  const handleClearAll = useCallback(() => setTempSelected(new Set()), []);

  return (
    <DSModal
      open={open}
      onClose={onClose}
      title={title}
      width={680}
      height={880}
    >
      <div className="ds-branch-modal">
        {/* === 좌측: (제품) 활성 상태 그룹 + 지역 탭 === */}
        <div className="ds-branch-modal__cities">
          <div className="ds-branch-modal__cities-list">
            {showActiveFilter && (
              <>
                <SectionHeader>활성 상태</SectionHeader>
                <CityMenuItem
                  name="활성 지점"
                  count={activeCount}
                  active={activeCity === "__active__"}
                  onClick={() => setActiveCity("__active__")}
                />
                <CityMenuItem
                  name="비활성 지점"
                  count={inactiveCount}
                  active={activeCity === "__inactive__"}
                  onClick={() => setActiveCity("__inactive__")}
                />
              </>
            )}
            {brandGroups.length > 0 && (
              <>
                <SectionHeader>브랜드</SectionHeader>
                {brandGroups.map((group) => (
                  <CityMenuItem
                    key={group.key}
                    name={group.name}
                    count={group.items.length}
                    active={activeCity === group.key}
                    onClick={() => setActiveCity(group.key)}
                  />
                ))}
              </>
            )}
            {(showActiveFilter || brandGroups.length > 0) && (
              <SectionHeader>지역</SectionHeader>
            )}
            {visibleCities.map((city) => (
              <CityMenuItem
                key={city.key}
                name={city.name}
                count={city.count}
                active={activeCity === city.key}
                onClick={() => setActiveCity(city.key)}
              />
            ))}
          </div>
          {/* 좌측 하단: "신규 지점 자동 포함" (제품 허용지점 전용). 전체 선택은 우측 패널에 있음. */}
          {onIncludeFutureChange && (
            <div className="ds-branch-modal__cities-footer">
              <DSCheckbox
                checked={!!tempIncludeFuture}
                onChange={setTempIncludeFuture}
                label="신규 지점 자동 포함"
              />
            </div>
          )}
        </div>

        {/* === 우측 === */}
        <div className="ds-branch-modal__right">
          <div className="ds-branch-modal__contents">
            <div className="ds-category-modal__tools">
              <DSSearchInput
                value={search}
                onChange={setSearch}
                placeholder={searchPlaceholder}
              />
              <div
                className="ds-category-modal__tools-row"
                style={{
                  justifyContent:
                    singleSelect || maxSelected != null
                      ? "flex-end"
                      : "space-between",
                }}
              >
                {/* 단일/상한 선택 모드에서는 전체 선택 미노출 */}
                {!singleSelect && maxSelected == null && (
                  <DSCheckbox
                    checked={isAllFilteredSelected}
                    onChange={handleSelectAllFiltered}
                    label="전체"
                  />
                )}
                {activeCity === "__all__" && regionGroups.length > 0 && (
                  <span
                    onClick={toggleCollapseAll}
                    style={{
                      cursor: "pointer",
                      fontSize: "var(--ds-font-size-12)",
                      fontWeight: "var(--ds-font-weight-semibold)",
                      color: "var(--ds-text-secondary)",
                    }}
                  >
                    {allCollapsed ? "모두 펼치기" : "모두 접기"}
                  </span>
                )}
              </div>
            </div>
            <div className="ds-category-modal__list">
              {activeCity === "__all__" ? (
                regionGroups.length === 0 ? (
                  <div
                    style={{
                      padding: 40,
                      textAlign: "center",
                      color: "var(--ds-text-tertiary)",
                      fontSize: "var(--ds-font-size-14)",
                      lineHeight: "var(--ds-line-height-22)",
                    }}
                  >
                    {emptyText}
                  </div>
                ) : (
                  regionGroups.map((region) => {
                    const sel = region.items.filter((l) =>
                      tempSelected.has(l.id),
                    ).length;
                    const isCollapsed = collapsed.has(region.key);
                    return (
                      <div key={region.key}>
                        <RegionHeader
                          name={region.name}
                          selectedCount={sel}
                          total={region.items.length}
                          checked={sel === region.items.length}
                          partial={sel > 0 && sel < region.items.length}
                          collapsed={isCollapsed}
                          onToggleAll={() => toggleRegionAll(region.items)}
                          onToggleCollapse={() => toggleCollapse(region.key)}
                          showCheckbox={!singleSelect && maxSelected == null}
                        />
                        {!isCollapsed &&
                          region.items.map((loc) => (
                            <BranchRow
                              key={loc.id}
                              location={loc}
                              isChecked={tempSelected.has(loc.id)}
                              onToggle={() => handleToggle(loc.id)}
                            />
                          ))}
                      </div>
                    );
                  })
                )
              ) : filteredLocations.length === 0 ? (
                <div
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: "var(--ds-text-tertiary)",
                    fontSize: "var(--ds-font-size-14)",
                    lineHeight: "var(--ds-line-height-22)",
                  }}
                >
                  {emptyText}
                </div>
              ) : (
                filteredLocations.map((loc) => (
                  <BranchRow
                    key={loc.id}
                    location={loc}
                    isChecked={tempSelected.has(loc.id)}
                    onToggle={() => handleToggle(loc.id)}
                  />
                ))
              )}
            </div>
          </div>
          <div className="ds-category-modal__actions">
            <DSButton
              variant="secondary"
              size="lg"
              onClick={handleClearAll}
              disabled={tempSelected.size === 0}
            >
              전체 해제
            </DSButton>
            <DSButton
              variant="primary"
              size="lg"
              onClick={handleApply}
              style={{ flex: 1 }}
            >
              선택 완료 ({tempSelected.size})
            </DSButton>
          </div>
        </div>
      </div>
    </DSModal>
  );
};

// 같은 트리 선택 UI 를 모달이 아닌 화면 안에 그리는 곳(재고조회 화면 설정의 조회 매장 선택)에서 재사용
export { KOREAN_CITIES, groupLocationsByCity, CityMenuItem, SectionHeader };

export default React.memo(BranchSelectModal);
